import {
	MAINTENANCE_DEFAULT_POLL_INTERVAL_MS,
	MAINTENANCE_MAX_POLL_INTERVAL_MS,
	MAINTENANCE_MIN_POLL_INTERVAL_MS
} from '~/utils/maintenance'

const STORAGE_KEY = 'apropos_jobs_maintenance_v1'

function canUseSessionStorage() {
	return typeof window !== 'undefined' && Boolean(window.sessionStorage)
}

function normalizePollInterval(value) {
	const interval = Number(value)

	if (!Number.isFinite(interval) || interval <= 0) {
		return MAINTENANCE_DEFAULT_POLL_INTERVAL_MS
	}

	return Math.min(
		MAINTENANCE_MAX_POLL_INTERVAL_MS,
		Math.max(MAINTENANCE_MIN_POLL_INTERVAL_MS, interval)
	)
}

function readStoredState() {
	if (!canUseSessionStorage()) {
		return null
	}

	try {
		const raw = window.sessionStorage.getItem(STORAGE_KEY)

		if (!raw) {
			return null
		}

		const parsed = JSON.parse(raw)

		if (!parsed || parsed.active !== true) {
			return null
		}

		return {
			active: true,
			intendedRoute:
				typeof parsed.intendedRoute === 'string'
					? parsed.intendedRoute
					: '/',
			retryAfterMs: normalizePollInterval(parsed.retryAfterMs)
		}
	} catch (error) {
		return null
	}
}

function writeStoredState(state) {
	if (!canUseSessionStorage()) {
		return
	}

	try {
		if (!state.active) {
			window.sessionStorage.removeItem(STORAGE_KEY)
			return
		}

		window.sessionStorage.setItem(
			STORAGE_KEY,
			JSON.stringify({
				active: true,
				intendedRoute: state.intendedRoute || '/',
				retryAfterMs: normalizePollInterval(state.retryAfterMs)
			})
		)
	} catch (error) {
		// Storage failures should never prevent the maintenance UI from rendering.
	}
}

export const state = () => {
	const storedState = readStoredState()

	return {
		active: Boolean(storedState && storedState.active),
		intendedRoute: storedState ? storedState.intendedRoute : null,
		retryAfterMs: storedState
			? storedState.retryAfterMs
			: MAINTENANCE_DEFAULT_POLL_INTERVAL_MS
	}
}

export const getters = {
	isActive(state) {
		return Boolean(state.active)
	}
}

export const mutations = {
	activate(state, payload = {}) {
		state.active = true
		state.intendedRoute =
			payload.intendedRoute || state.intendedRoute || '/'
		state.retryAfterMs = Object.prototype.hasOwnProperty.call(
			payload,
			'retryAfterMs'
		)
			? normalizePollInterval(payload.retryAfterMs)
			: normalizePollInterval(state.retryAfterMs)
		writeStoredState(state)
	},

	updateIntendedRoute(state, payload = {}) {
		if (!state.active || !payload.intendedRoute) {
			return
		}

		state.intendedRoute = payload.intendedRoute
		writeStoredState(state)
	},

	recover(state) {
		state.active = false
		state.intendedRoute = null
		state.retryAfterMs = MAINTENANCE_DEFAULT_POLL_INTERVAL_MS
		writeStoredState(state)
	}
}

export const actions = {
	activate({ commit }, payload) {
		commit('activate', payload)
	},

	updateIntendedRoute({ commit }, payload) {
		commit('updateIntendedRoute', payload)
	},

	recover({ commit }) {
		commit('recover')
	}
}
